import { PaymentPlan } from '../models/enums/payment-plan';

export interface EnvironmentPaymentSettings {
  title: string;
  price: number | string;
  buttonText: string;
  fixedButtonText?: string;
  badgeLabel?: string;
  description?: string;
  paymentPlan: PaymentPlan;
  subtitle: string;
  billingCadence?: 'month' | 'minute';
  priceUnitLabel?: string;
  highlightText?: string;
  highlightSubtext?: string;
  customFeaturesTitle?: string;
  features?: string[];
  compliance?: string[];
  isCustom?: boolean;
  bookingUrl?: string;
  costStructure: { minutesIncluded: number, additionalCostsPerMin: number };
  whiteLabel?: { whiteLabel: boolean, rebilling: boolean };
  nbAssistants: string;
  actions: { sms: boolean, booking: boolean };
  integrations: { zapier: boolean, cal: boolean };
  support: { emailSupport: boolean, whiteGloveOnboarding: boolean, dedicatedSupportManager: boolean, earlyReleaseFeatures: boolean };
  customVoice: { elevenLabs: boolean, playHT: boolean };
  receptionistOptions?: {
    callForwarding: boolean;
    callScreening: boolean;
    billingual: boolean;
    notifications: boolean;
    dashboard: boolean;
    smsAgent: boolean;
    customIntegrations: boolean;
    customVoices: boolean;
    advancedAnalytics: boolean;
    manyIntegrations: boolean;
  }
}

export const defaultPaymentPlans: EnvironmentPaymentSettings[] = [
  {
    title: 'Pay per use Plan',
    price: '0.10',
    buttonText: '',
    subtitle: '',
    billingCadence: 'minute',
    features: [
      'Voice ai building platform',
      'All Integrations Included',
      'Multi-Channel (Voice, SMS, Chat)',
      'Priority Support'
    ],
    paymentPlan: PaymentPlan.BASIC,
    costStructure: {
      minutesIncluded: 0,
      additionalCostsPerMin: 0.10
    },
    whiteLabel: {
      whiteLabel: true,
      rebilling: true
    },
    nbAssistants: '100',
    actions: {
      sms: true,
      booking: true
    },
    integrations: {
      zapier: true,
      cal: true
    },
    support: {
      emailSupport: true,
      whiteGloveOnboarding: true,
      dedicatedSupportManager: true,
      earlyReleaseFeatures: true
    },
    customVoice: {
      elevenLabs: true,
      playHT: true
    }
  },
  {
    title: 'White Labeling',
    price: 99,
    buttonText: '',
    subtitle: '',
    billingCadence: 'month',
    features: [
      'Custom Domain & Full Branding',
      'Stripe Integration & Automated Rebilling',
      'All Integrations Included',
      'Multi-Channel (Voice, SMS, Chat)',
      'Unlimited Sub-accounts',
      'Priority Support'
    ],
    paymentPlan: PaymentPlan.WHITELABEL,
    costStructure: {
      minutesIncluded: 1000,
      additionalCostsPerMin: 0.10
    },
    whiteLabel: {
      whiteLabel: true,
      rebilling: true
    },
    nbAssistants: '100',
    actions: {
      sms: true,
      booking: true
    },
    integrations: {
      zapier: true,
      cal: true
    },
    support: {
      emailSupport: true,
      whiteGloveOnboarding: true,
      dedicatedSupportManager: true,
      earlyReleaseFeatures: true
    },
    customVoice: {
      elevenLabs: true,
      playHT: true
    }
  },
  {
    title: 'Enterprise',
    description: 'Need help to get started?',
    price: 'Custom',
    priceUnitLabel: '',
    buttonText: 'Book an appointment',
    fixedButtonText: 'Book an appointment',
    subtitle: '',
    billingCadence: 'month',
    bookingUrl: 'https://calendly.com/pulsyaiservice/30min?month=2026-02',
    features: [
      'Everything in White Labeling',
      'Custom Setup & Onboarding',
      'Custom Workflows & Automations',
      'Dedicated Account Manager',
      'Volume-Based Pricing',
      'SLA & Uptime Guarantees'
    ],
    paymentPlan: PaymentPlan.ENTREPRISE,
    costStructure: {
      minutesIncluded: 0,
      additionalCostsPerMin: 0
    },
    whiteLabel: {
      whiteLabel: true,
      rebilling: true
    },
    nbAssistants: 'Custom',
    actions: {
      sms: true,
      booking: true
    },
    integrations: {
      zapier: true,
      cal: true
    },
    support: {
      emailSupport: true,
      whiteGloveOnboarding: true,
      dedicatedSupportManager: true,
      earlyReleaseFeatures: true
    },
    customVoice: {
      elevenLabs: true,
      playHT: true
    }
  }
];

export const defaultReceptionistPaymentPlans: EnvironmentPaymentSettings[] = [
  {
    title: 'Basic',
    price: 49,
    priceUnitLabel: 'USD',
    buttonText: 'Start Free Trial',
    fixedButtonText: 'Start Free Trial',
    badgeLabel: 'MOST POPULAR',
    highlightText: '150 minutes included',
    highlightSubtext: '$0.30 per extra minute',
    subtitle: 'per month',
    paymentPlan: PaymentPlan.STARTER_PRO,
    costStructure: {
      minutesIncluded: 150,
      additionalCostsPerMin: 0.30
    },
    whiteLabel: {
      whiteLabel: false,
      rebilling: false
    },
    nbAssistants: '1',
    actions: {
      sms: true,
      booking: true
    },
    integrations: {
      zapier: true,
      cal: true
    },
    support: {
      emailSupport: true,
      whiteGloveOnboarding: false,
      dedicatedSupportManager: false,
      earlyReleaseFeatures: false
    },
    customVoice: {
      elevenLabs: true,
      playHT: true
    },
    features: [
      'Appointment booking & calendar sync',
      'Call forwarding to staff/departments',
      '25 simultaneous calls',
      'Custom AI script training',
      'Intelligent call screening',
      'Bilingual (English/French)',
      'Email notifications',
      'Call analytics dashboard',
      'Email support',
      '7-day free trial'
    ],
    receptionistOptions: {
      callForwarding: true,
      callScreening: true,
      billingual: true,
      notifications: true,
      dashboard: true,
      smsAgent: false,
      customIntegrations: false,
      customVoices: false,
      advancedAnalytics: false,
      manyIntegrations: false,
    }
  },
  {
    title: 'Custom',
    description: 'built for your needs',
    price: 0,
    priceUnitLabel: '',
    buttonText: 'Contact Sales',
    fixedButtonText: 'Contact Sales',
    highlightText: 'Unlimited calls & minutes',
    highlightSubtext: 'Custom pricing',
    customFeaturesTitle: 'Everything in Basic plus:',
    compliance: ['PIPEDA', 'HIPAA-Ready', 'Encrypted', 'SOC 2'],
    subtitle: 'Custom pricing',
    isCustom: true,
    paymentPlan: PaymentPlan.RECEPTIONIST_ENTREPRISE,
    costStructure: {
      minutesIncluded: 0,
      additionalCostsPerMin: 0
    },
    whiteLabel: {
      whiteLabel: true,
      rebilling: true
    },
    nbAssistants: '5',
    actions: {
      sms: true,
      booking: true
    },
    integrations: {
      zapier: true,
      cal: true
    },
    support: {
      emailSupport: true,
      whiteGloveOnboarding: true,
      dedicatedSupportManager: true,
      earlyReleaseFeatures: true
    },
    customVoice: {
      elevenLabs: true,
      playHT: true
    },
    features: [
      'SMS AI Agent',
      'Whitelabel options',
      'Premium CRM integrations (HubSpot, Salesforce, Google & more)',
      'Custom integrations',
      '50+ simultaneous calls',
      'Advanced analytics & reporting',
      'API access',
      'Priority support',
      'Dedicated onboarding'
    ],
    receptionistOptions: {
      callForwarding: true,
      callScreening: true,
      billingual: true,
      notifications: true,
      dashboard: true,
      smsAgent: false,
      customIntegrations: false,
      customVoices: true,
      advancedAnalytics: true,
      manyIntegrations: true,
    }
  },
];
